import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export type ProductPaymentDirection = "debit" | "credit";

export type ProductPaymentTransactionType =
  | "purchase"
  | "investment"
  | "additional_cost"
  | "sale"
  | "token"
  | "installment"
  | "refund"
  | "adjustment";

export type ProductPaymentMethod = "cash" | "bank" | "cheque" | "online";

export type ProductPaymentRecord = {
  id: number;
  product_id: number;
  sale_id?: number | null;
  party_id?: number | null;
  party_name?: string | null;
  /** Party role from parties.type: supplier | investor | customer | all */
  party_type?: string | null;
  direction: ProductPaymentDirection;
  amount: number;
  debit: number;
  credit: number;
  transaction_type: ProductPaymentTransactionType | null;
  transaction_type_label: string | null;
  payment_date: string | null;
  payment_method?: ProductPaymentMethod | null;
  payment_method_label?: string | null;
  bank_id?: number | null;
  bank_name?: string | null;
  transaction_reference_no?: string | null;
  cheque_no?: string | null;
  pay_order_no?: string | null;
  notes?: string | null;
  reference_type?: string | null;
  reference_id?: number | null;
  scheduled_payment_id?: number | null;
  product_invoice_id?: number | null;
  currency_code?: string | null;
  currency_amount?: number | null;
  exchange_rate?: number | null;
  created_at?: string | null;
};

const SCHEDULED_REFERENCE_TYPES = new Set([
  "product",
  "purchase_cost_line",
  "deal_partner",
  "deal_partner_base",
  "deal_partner_cost_line",
]);

export function isScheduledProductPayment(row: ProductPaymentRecord): boolean {
  return SCHEDULED_REFERENCE_TYPES.has(row.reference_type || "");
}

export function isActualProductPayment(row: ProductPaymentRecord): boolean {
  return row.reference_type === "journal_entry";
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function normalizeScheduleLabel(label: string): string {
  return label.trim().toLowerCase().replace(/\s+/g, " ");
}

function schedulePurpose(row: ProductPaymentRecord): string {
  const notes = row.notes?.trim();
  if (notes) return notes;
  return row.transaction_type_label?.trim() || "";
}

function scheduleAmount(row: ProductPaymentRecord): number {
  return round2(Number(row.amount) || Math.max(Number(row.debit), Number(row.credit)));
}

function scheduleMatchKey(row: ProductPaymentRecord): string {
  return `${normalizeScheduleLabel(schedulePurpose(row))}|${scheduleAmount(row).toFixed(2)}`;
}

/**
 * Scheduled rows folded into an invoice collective: any row with product_invoice_id,
 * plus investor/deal-partner receivables that mirror invoice-linked payable lines
 * (same purpose + amount) when those receivables were created without an invoice id.
 */
export function buildInvoiceCoveredScheduledIds(
  scheduled: ProductPaymentRecord[],
): Set<number> {
  const covered = new Set<number>();
  const invoicePayableKeys = new Map<string, string>();

  for (const row of scheduled) {
    const invId = row.product_invoice_id != null ? Number(row.product_invoice_id) : 0;
    if (invId <= 0) continue;
    covered.add(row.id);
    if (row.direction === "credit") {
      invoicePayableKeys.set(scheduleMatchKey(row), String(invId));
    }
  }

  for (const row of scheduled) {
    if (covered.has(row.id)) continue;
    if (row.direction === "credit") continue;
    const invId = invoicePayableKeys.get(scheduleMatchKey(row));
    if (invId) covered.add(row.id);
  }

  return covered;
}

/** Line-level journal settlement against an invoice-covered schedule (exclude from UI/cash). */
export function isInvoiceLineLevelActual(
  row: ProductPaymentRecord,
  coveredScheduledIds: Set<number>,
): boolean {
  if (!isActualProductPayment(row)) return false;
  if (row.product_invoice_id != null && Number(row.product_invoice_id) > 0) return false;
  const schedId = row.scheduled_payment_id != null ? Number(row.scheduled_payment_id) : 0;
  return schedId > 0 && coveredScheduledIds.has(schedId);
}

function amountAppliedToScheduledRow(
  payment: ProductPaymentRecord,
  scheduledRow: ProductPaymentRecord,
): number {
  const isPayable = scheduledRow.direction === "credit";
  return isPayable ? Number(payment.debit) : Number(payment.credit);
}

function invoiceScheduleTargets(
  scheduled: ProductPaymentRecord[],
  invoiceId: string | number,
  isPay: boolean,
): ProductPaymentRecord[] {
  const inv = String(invoiceId);
  const direct = scheduled.filter((row) => {
    if (String(row.product_invoice_id ?? "") !== inv) return false;
    return isPay ? row.direction === "credit" : row.direction !== "credit";
  });
  if (isPay || direct.length > 0) return direct;

  // Receivables often lack product_invoice_id — match purpose+amount to this invoice's payables.
  const payableKeys = new Set(
    scheduled
      .filter((row) => row.direction === "credit" && String(row.product_invoice_id ?? "") === inv)
      .map((row) => scheduleMatchKey(row)),
  );
  if (payableKeys.size === 0) return [];

  return scheduled.filter(
    (row) => row.direction !== "credit" && payableKeys.has(scheduleMatchKey(row)),
  );
}

/** Map each scheduled payment row id -> total amount already paid/received against it. */
export function buildScheduledPaymentAllocations(
  scheduled: ProductPaymentRecord[],
  actual: ProductPaymentRecord[],
): Map<number, number> {
  const allocations = new Map<number, number>();
  for (const row of scheduled) {
    allocations.set(row.id, 0);
  }

  const journalActual = actual.filter(isActualProductPayment);

  for (const payment of journalActual) {
    if (payment.scheduled_payment_id != null) {
      const row = scheduled.find((candidate) => candidate.id === payment.scheduled_payment_id);
      if (!row) {
        continue;
      }
      const prev = allocations.get(row.id) ?? 0;
      allocations.set(row.id, prev + amountAppliedToScheduledRow(payment, row));
      continue;
    }

    const isPay = Number(payment.debit) > Number(payment.credit);
    const amount = isPay ? Number(payment.debit) : Number(payment.credit);
    if (amount <= 0) {
      continue;
    }

    if (payment.product_invoice_id != null) {
      const invoiceScheduledRows = invoiceScheduleTargets(
        scheduled,
        payment.product_invoice_id,
        isPay,
      );
      let remaining = amount;
      for (const row of invoiceScheduledRows) {
        const allocated = allocations.get(row.id) ?? 0;
        const need = Math.max(0, Number(row.amount) - allocated);
        const apply = Math.min(remaining, need);
        allocations.set(row.id, allocated + apply);
        remaining -= apply;
        if (remaining <= 0.009) {
          break;
        }
      }
      continue;
    }

    const candidates = scheduled.filter((row) => {
      const rowIsPayable = row.direction === "credit";
      if (isPay !== rowIsPayable) {
        return false;
      }
      if (row.party_id !== payment.party_id) {
        return false;
      }
      if (row.transaction_type !== payment.transaction_type) {
        return false;
      }
      const allocated = allocations.get(row.id) ?? 0;
      return allocated + 0.009 < Number(row.amount);
    });

    let remaining = amount;
    for (const row of candidates) {
      const allocated = allocations.get(row.id) ?? 0;
      const need = Math.max(0, Number(row.amount) - allocated);
      const apply = Math.min(remaining, need);
      allocations.set(row.id, allocated + apply);
      remaining -= apply;
      if (remaining <= 0.009) {
        break;
      }
    }
  }

  return allocations;
}

export function scheduledRowOutstanding(
  row: ProductPaymentRecord,
  allocations: Map<number, number>,
): number {
  const allocated = allocations.get(row.id) ?? 0;
  return Math.max(0, Number(row.amount) - allocated);
}

export type ExtraPaymentRecord = {
  id: number;
  product_id: number;
  party_id?: number | null;
  party_name?: string | null;
  product_payment_id?: number | null;
  scheduled_payment_id?: number | null;
  cost_label?: string | null;
  actual_amount: number;
  received_amount: number;
  profit_amount: number;
  action?: string | null;
  transaction_type?: string | null;
  payment_date?: string | null;
  payment_method?: string | null;
  bank_id?: number | null;
  notes?: string | null;
  created_at?: string | null;
};

export type ProductPaymentsResponse = {
  data: ProductPaymentRecord[];
  extra_payments: ExtraPaymentRecord[];
  meta: {
    total_debit: number;
    total_credit: number;
    net: number;
  };
};

export function productPaymentErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong. Please try again.";
}

export async function fetchProductPayments(productId: string | number): Promise<ProductPaymentsResponse> {
  const response = await api.get<ProductPaymentsResponse>(
    `/api/v1/catalog/products/${encodeURIComponent(String(productId))}/payments`,
  );
  return {
    data: Array.isArray(response.data?.data) ? response.data.data : [],
    extra_payments: Array.isArray(response.data?.extra_payments) ? response.data.extra_payments : [],
    meta: {
      total_debit: Number(response.data?.meta?.total_debit ?? 0),
      total_credit: Number(response.data?.meta?.total_credit ?? 0),
      net: Number(response.data?.meta?.net ?? 0),
    },
  };
}

export type InvestorBalanceResponse = {
  party_id: number;
  account_number: string;
  account_name: string;
  balance: number;
};

export async function fetchInvestorBalance(
  productId: string | number,
  partyId: number,
): Promise<InvestorBalanceResponse> {
  const response = await api.get<{ data: InvestorBalanceResponse }>(
    `/api/v1/catalog/products/${encodeURIComponent(String(productId))}/investor-balance`,
    { params: { party_id: partyId } },
  );
  const data = response.data?.data;
  return {
    party_id: Number(data?.party_id ?? partyId),
    account_number: String(data?.account_number ?? "220401"),
    account_name: String(data?.account_name ?? "Deal Partners Payable"),
    balance: Math.round(Number(data?.balance ?? 0) * 100) / 100,
  };
}

export async function recordProductPayment(
  productId: string | number,
  payload: {
    amount: number;
    payment_date: string;
    payment_method: string | null;
    funding_source?: "cash_bank" | "investor_balance";
    bank_id?: number | null;
    bank_name?: string | null;
    transaction_reference_no: string | null;
    pay_order_no: string | null;
    cheque_no?: string | null;
    deposit_slip_no?: string | null;
    notes: string | null;
    action: "pay" | "receive";
    party_id?: number;
    transaction_type?: string | null;
    scheduled_payment_id?: number | null;
    product_invoice_id?: number | null;
    currency_code?: string | null;
    currency_amount?: number | null;
    exchange_rate?: number | null;
    settlement_mode?: "cash_pay" | "reinvest";
    profit_distribution_id?: number | null;
  }
): Promise<any> {
  const response = await api.post(`/api/v1/catalog/products/${encodeURIComponent(String(productId))}/payments`, payload);
  return response.data;
}
